/**
 * Client error reporting → GTM dataLayer.
 *
 * Until now a crash in the estimator was caught by the wizard's error boundary
 * and thrown away: the visitor saw "Something went wrong" and nobody else saw
 * anything. Every such crash, plus uncaught errors and unhandled promise
 * rejections, is now pushed as ONE dataLayer event so GTM can forward it to
 * GA4 and Clarity. Nothing in this module is user-facing.
 *
 * dataLayer event shape (wire a GTM trigger on event = 'estimator_error'):
 *   event:            'estimator_error'
 *   error_scope:      'wizard' | '3d-viewer' | 'window' | 'promise' | 'turnstile'
 *   error_name:       e.g. 'TypeError', 'ChunkLoadError'
 *   error_message:    first 300 chars
 *   error_stack:      first few frames, 600 chars max
 *   error_chunk_load: true when a lazy chunk 404'd (stale tab after a deploy)
 *   component_stack:  the React component that threw, when known
 *   wizard_step:      'mode' | 'walls' | … | 'results' | null
 *   page_path:        location.pathname
 *   app_build:        BUILD_NUMBER
 *   user_agent:       navigator.userAgent
 *
 * A second, quieter event exists for things that are not bugs but device
 * limits (no WebGL, no Fullscreen API): event = 'estimator_capability' with
 * `feature` and the same page/build/agent keys. They used to be reported as
 * errors and drowned the real ones.
 *
 * Reports are de-duplicated per page load and capped, so a render loop can't
 * flood analytics.
 */

import { BUILD_NUMBER } from '../version';

declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

const MAX_REPORTS_PER_PAGE = 20;
const MESSAGE_LIMIT = 300;
const STACK_LIMIT = 600;

const seen = new Set<string>();
let reportCount = 0;
let currentStep: string | null = null;
let installed = false;

export type ErrorScope = 'wizard' | '3d-viewer' | 'window' | 'promise' | 'turnstile';

interface ReportOptions {
  scope: ErrorScope;
  error: unknown;
  extra?: Record<string, unknown>;
}

/** The wizard calls this as the visitor moves, so reports carry the step. */
export function setErrorReportingStep(step: string | null): void {
  currentStep = step;
}

function toError(value: unknown): { name: string; message: string; stack: string } {
  if (value instanceof Error) {
    return { name: value.name || 'Error', message: value.message || '', stack: value.stack || '' };
  }
  if (value && typeof value === 'object') {
    const o = value as { name?: unknown; message?: unknown; stack?: unknown };
    return {
      name: typeof o.name === 'string' ? o.name : 'Error',
      message: typeof o.message === 'string' ? o.message : safeString(value),
      stack: typeof o.stack === 'string' ? o.stack : '',
    };
  }
  return { name: 'Error', message: safeString(value), stack: '' };
}

function safeString(value: unknown): string {
  try {
    return typeof value === 'string' ? value : JSON.stringify(value) ?? String(value);
  } catch {
    return String(value);
  }
}

/**
 * A lazy `import()` whose chunk no longer exists on the server. Happens when a
 * tab opened before a deploy later loads a code-split chunk by its old hash.
 * The fix is a reload, not an error screen.
 */
export function isChunkLoadError(err: unknown): boolean {
  const e = toError(err);
  if (e.name === 'ChunkLoadError') return true;
  return /Loading (CSS )?chunk [\w-]+ failed/i.test(e.message)
    || /dynamically imported module/i.test(e.message)
    || /Importing a module script failed/i.test(e.message);
}

/** Three.js could not get a WebGL context: a device limit, not a bug. */
export function isWebGLUnavailableError(err: unknown): boolean {
  const e = toError(err);
  return /creating (a )?WebGL context/i.test(e.message)
    || /WebGL (is )?not supported/i.test(e.message)
    || /WebGL.*unavailable/i.test(e.message);
}

/**
 * Browser and extension noise that is not ours to fix. Each pattern is a known
 * benign message:
 *   - ResizeObserver's "loop completed" notice is a browser heads-up, not an error.
 *   - runtime.sendMessage / webkit.messageHandlers / "Extension context
 *     invalidated" come from browser extensions injected into the page.
 *   - Scripts served from an extension URL are never our code.
 */
const IGNORED_MESSAGE_PATTERNS: RegExp[] = [
  /ResizeObserver loop (completed with undelivered notifications|limit exceeded)/i,
  /runtime\.sendMessage/i,
  /window\.webkit\.messageHandlers/i,
  /Extension context invalidated/i,
  /chrome-extension:\/\//i,
  /moz-extension:\/\//i,
  /safari-web-extension:\/\//i,
];

export function isIgnorableClientError(err: unknown, sourceFile?: string): boolean {
  if (sourceFile && /^(chrome|moz|safari-web)-extension:\/\//i.test(sourceFile)) return true;
  const e = toError(err);
  const haystack = `${e.message}\n${e.stack}`;
  return IGNORED_MESSAGE_PATTERNS.some((re) => re.test(haystack));
}

const RELOAD_KEY = 'trusscore.chunkReloadAt';

/**
 * Reload once to pick up the current build. Returns false (and does nothing)
 * if a reload was already attempted in the last minute, so a genuinely broken
 * deploy can't put the visitor in a reload loop. The wizard's sessionStorage
 * snapshot survives the reload, so their inputs come back.
 */
export function reloadForStaleChunk(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) || 0);
    if (Date.now() - last < 60_000) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
  } catch {
    // No storage means no loop guard; fall through to the error screen instead.
    return false;
  }
  window.location.reload();
  return true;
}

function commonKeys(): Record<string, unknown> {
  return {
    wizard_step: currentStep,
    page_path: window.location.pathname,
    app_build: BUILD_NUMBER,
    user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
  };
}

/** Push one error to the dataLayer. Safe to call from anywhere, any time. */
export function reportClientError({ scope, error, extra }: ReportOptions): void {
  try {
    if (isIgnorableClientError(error, typeof extra?.source_file === 'string' ? extra.source_file : undefined)) return;
    const e = toError(error);
    const key = `${scope}|${e.name}|${e.message}`;
    if (seen.has(key) || reportCount >= MAX_REPORTS_PER_PAGE) return;
    seen.add(key);
    reportCount += 1;

    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'estimator_error',
      error_scope: scope,
      error_name: e.name,
      error_message: e.message.slice(0, MESSAGE_LIMIT),
      error_stack: e.stack.slice(0, STACK_LIMIT),
      error_chunk_load: isChunkLoadError(error),
      ...commonKeys(),
      ...extra,
    });
  } catch {
    // Reporting must never itself become a crash.
  }
}

/**
 * Record a device/browser limit the app handled gracefully (placeholder shown,
 * button hidden). Counted separately from errors so the error report stays a
 * list of bugs. Once per feature per page.
 */
export function reportCapabilityGap(feature: 'webgl' | 'fullscreen', detail?: unknown): void {
  try {
    const key = `capability|${feature}`;
    if (seen.has(key)) return;
    seen.add(key);
    if (typeof window === 'undefined') return;
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'estimator_capability',
      feature,
      detail: detail === undefined ? '' : toError(detail).message.slice(0, MESSAGE_LIMIT),
      ...commonKeys(),
    });
  } catch {
    // Never crash on reporting.
  }
}

/**
 * Catch what no error boundary sees: errors thrown outside React's render and
 * effect phases (event handlers, timers, async work) and rejected promises.
 * Idempotent; call once from the entry point.
 */
export function installGlobalErrorHandlers(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (ev: ErrorEvent) => {
    // Resource-load failures don't reach a non-capturing window listener, so
    // anything here is a script error. Skip the opaque cross-origin "Script
    // error." with no detail — it carries nothing actionable.
    if (!ev.error && (!ev.message || ev.message === 'Script error.')) return;
    reportClientError({
      scope: 'window',
      error: ev.error ?? new Error(ev.message),
      extra: { source_file: ev.filename || '', source_line: ev.lineno || 0 },
    });
  });

  window.addEventListener('unhandledrejection', (ev: PromiseRejectionEvent) => {
    reportClientError({ scope: 'promise', error: ev.reason });
  });
}
